import { createApi } from "@reduxjs/toolkit/query/react";
import type { IApiResponse } from "@/types";
import type { IGetFilesUploadQRCodeResponse } from "@/types/upload.interface";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { APIEndpoints } from "@/constants/apiEndpoints";
import toast from "react-hot-toast";
import type { TempFileUploadResponse } from "@/types/file-upload.interface";

export const fileUploadApi = createApi({
  reducerPath: "fileUploadApi",
  baseQuery: dynamicBaseQuery,
  endpoints: (builder) => ({
    uploadTemporaryFile: builder.mutation<IApiResponse<TempFileUploadResponse>, { file: File }>({
      query: ({ file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: APIEndpoints.Common.FileUpload,
          method: "POST",
          body: formData,
        };
      },
      async onQueryStarted(_, { queryFulfilled }) {
        const toastId = toast.loading("Uploading file...");
        try {
          await queryFulfilled;
          toast.success("File uploaded successfully", { id: toastId });
        } catch (error) {
          console.error("File upload failed:", error);
          toast.error("Failed to upload file", { id: toastId });
        }
      },
    }),
    getFilesUploadQRCode: builder.query<
      IApiResponse<IGetFilesUploadQRCodeResponse>,
      { fileType: string; moduleType: string }
    >({
      query: (data) => ({
        url: "/qr-file-upload/generate-file-upload-qr-code",
        method: "POST",
        body: data,
      }),
    }),
    validateQRCodeSecret: builder.query<
      IApiResponse<{ status: "scanned" | "uploaded" | "expired"; fileType: string; expiredAt: number }>,
      { secret: string }
    >({
      query: (data) => ({
        url: "/qr-file-upload/status",
        headers: {
          Authorization: `Bearer ${data.secret}`,
        },
      }),
    }),
    submitQRCodeFiles: builder.mutation<IApiResponse<void>, { secret: string; formData: FormData }>({
      query: ({ secret, formData }) => ({
        url: "/qr-file-upload/upload",
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${secret}`,
        },
      }),
    }),
  }),
});

export const {
  useUploadTemporaryFileMutation,
  useGetFilesUploadQRCodeQuery,
  useValidateQRCodeSecretQuery,
  useSubmitQRCodeFilesMutation,
} = fileUploadApi;
